import { useCallback, useEffect, useRef, useState } from 'react'

import {
  parseAggregateInboxPage,
  parseInboxPage,
  type InboxPage,
} from './conversations-pagination'
import {
  errorKind,
  type ConversationRepository,
} from './conversations-repository'

export type ConversationSearchStatus =
  'idle' | 'unsupported' | 'loading' | 'ready' | 'forbidden' | 'error'

type SearchState = {
  key: string | null
  status: ConversationSearchStatus
  pages: InboxPage[]
  loadingOlder: boolean
  olderFailed: boolean
}

const idle: SearchState = {
  key: null,
  status: 'idle',
  pages: [],
  loadingOlder: false,
  olderFailed: false,
}

/**
 * Pages of the clinic's whole conversation list that match `query`, read from
 * the same list as the inbox (`mode`). Results of a superseded query, tenant
 * or list are discarded, never shown for the current one.
 */
export function useConversationSearch(
  repository: ConversationRepository,
  tenantId: string,
  mode: 'legacy' | 'canonical' | null,
  query: string
) {
  const generation = useRef(0)
  const [attempt, setAttempt] = useState(0)
  const [state, setState] = useState<SearchState>(idle)
  const search =
    mode === 'canonical'
      ? repository.searchAggregateInbox
      : mode === 'legacy'
        ? repository.searchInbox
        : undefined
  const parse = mode === 'canonical' ? parseAggregateInboxPage : parseInboxPage
  const key = query && mode ? JSON.stringify([tenantId, mode, query]) : null

  useEffect(() => {
    const currentGeneration = ++generation.current
    if (!key || !search) return
    setState({ ...idle, key, status: 'loading' })
    search(tenantId, query, null).then(
      (value) => {
        if (generation.current !== currentGeneration) return
        setState({ ...idle, key, status: 'ready', pages: [parse(value)] })
      },
      (error: unknown) => {
        if (generation.current !== currentGeneration) return
        setState({
          ...idle,
          key,
          status: errorKind(error) === 'forbidden' ? 'forbidden' : 'error',
        })
      }
    )
    return () => {
      generation.current += 1
    }
  }, [attempt, key, parse, query, search, tenantId])

  const effectiveState: SearchState =
    mode !== null && !search
      ? { ...idle, status: 'unsupported' }
      : key === null
        ? idle
        : state.key === key
          ? state
          : { ...idle, key, status: 'loading' }
  const lastPage = effectiveState.pages.at(-1) ?? null

  const loadOlder = useCallback(async () => {
    if (
      !search ||
      effectiveState.status !== 'ready' ||
      effectiveState.loadingOlder ||
      !lastPage?.hasOlder ||
      !lastPage.cursor
    ) {
      return
    }
    const requestGeneration = generation.current
    setState((current) => ({
      ...current,
      loadingOlder: true,
      olderFailed: false,
    }))
    try {
      const value = await search(tenantId, query, lastPage.cursor)
      if (generation.current !== requestGeneration) return
      const page = parse(value)
      setState((current) => ({
        ...current,
        pages: [...current.pages, page],
        loadingOlder: false,
      }))
    } catch {
      if (generation.current !== requestGeneration) return
      // The results already shown stay; the next attempt retries this page.
      setState((current) => ({
        ...current,
        loadingOlder: false,
        olderFailed: true,
      }))
    }
  }, [
    effectiveState.loadingOlder,
    effectiveState.status,
    lastPage,
    parse,
    query,
    search,
    tenantId,
  ])

  const retry = useCallback(() => setAttempt((count) => count + 1), [])

  return {
    status: effectiveState.status,
    pages: effectiveState.pages,
    hasOlder:
      effectiveState.status === 'ready' && (lastPage?.hasOlder ?? false),
    loadingOlder: effectiveState.loadingOlder,
    olderFailed: effectiveState.olderFailed,
    loadOlder,
    retry,
  }
}
